/**
 * Zustand store for the globally-mounted `SyncDataProgressDialog`
 * (Database Blueprint Slice 10c).
 *
 * The SyncDataDialog's Confirm hand-off opens this dialog through
 * `useSyncDataProgressDialogStore.getState().openSyncDataProgressDialog(...)`.
 * The progress dialog itself is short-lived per Confirm — the store
 * carries the full arg payload so any caller can launch a fresh run
 * (e.g. a retry button in the future) without re-resolving the link
 * mapping.
 *
 * Blueprints the lightweight shape used by other Database Blueprint dialogs.
 */

import { create } from 'zustand';
import type { UserFacingEngineId } from '../../../database-blueprint/types/databaseBlueprintTypes';
import type { SyncDataProgressTableEntry } from '../components/SyncDataProgressDialog';

export type SyncDataProgressDialogArgs = {
  folderPath: string;
  link: {
    connectionId: string;
    database: string;
    engine: UserFacingEngineId;
  };
  /** Ordered (FK or alphabetical from Slice 10b) selected tables. */
  tables: ReadonlyArray<SyncDataProgressTableEntry>;
};

export type SyncDataProgressDialogState =
  | { open: false }
  | ({ open: true } & SyncDataProgressDialogArgs);

export interface SyncDataProgressDialogStore {
  state: SyncDataProgressDialogState;
  openSyncDataProgressDialog: (args: SyncDataProgressDialogArgs) => void;
  closeSyncDataProgressDialog: () => void;
}

export const useSyncDataProgressDialogStore =
  create<SyncDataProgressDialogStore>((set) => ({
    state: { open: false },
    openSyncDataProgressDialog: (args) =>
      set({ state: { open: true, ...args } }),
    closeSyncDataProgressDialog: () => set({ state: { open: false } }),
  }));
